import { useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MoreVertical, Plus, Search, Pencil, Building2, User2, Users } from "lucide-react";

import { apiFetch } from "@/lib/api";
import { PairingForm, Option, PairingFormState } from "./dialog/PairingForm";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

type CursorMeta = {
  per_page?: number;
  next_cursor: string | null;
  prev_cursor?: string | null;
  has_more: boolean;
};

type CursorResponse<T> = {
  data: T[];
  meta: CursorMeta;
  anchor_id?: number | null;
};

type ApiListResponse<T> = { data: T[] };

type PairingRow = {
  id: number;
  branch_id: number;
  username1: number;
  username2: number;

  branch?: { id: number; name: string } | null;
  loan_officer?: { id: number; username: string; firstname?: string; lastname?: string } | null;
  collection_officer?: { id: number; username: string; firstname?: string; lastname?: string } | null;

  active_customers?: any[];
  dormant_customers?: any[];
};

function clsx(...s: Array<string | false | undefined | null>) {
  return s.filter(Boolean).join(" ");
}

function useDebouncedValue<T>(value: T, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

/** ✅ Infinite-scroll sentinel (viewport root prevents “flipping”) */
function useInfiniteSentinel(opts: { enabled: boolean; onLoadMore: () => void }) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!opts.enabled) return;
    const el = sentinelRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) opts.onLoadMore();
      },
      { root: null, rootMargin: "520px", threshold: 0.01 }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [opts.enabled, opts.onLoadMore]);

  return sentinelRef;
}

function SkeletonRow({ rtl }: { rtl: boolean }) {
  return (
    <tr className="border-b">
      <td className="py-4 px-3 text-muted-foreground">—</td>
      <td className="py-4 px-3"><div className="h-4 w-32 rounded bg-muted animate-pulse" /></td>
      <td className="py-4 px-3"><div className="h-4 w-40 rounded bg-muted animate-pulse" /></td>
      <td className="py-4 px-3"><div className="h-4 w-40 rounded bg-muted animate-pulse" /></td>
      <td className="py-4 px-3"><div className="h-4 w-20 rounded bg-muted animate-pulse" /></td>
      <td className="py-4 px-3"><div className="h-4 w-20 rounded bg-muted animate-pulse" /></td>
      <td className={clsx("py-4 px-3", rtl ? "text-left" : "text-right")}>
        <div className="inline-block h-8 w-10 rounded bg-muted animate-pulse" />
      </td>
    </tr>
  );
}

function PairingCard({
  r,
  rtl,
  onEdit,
}: {
  r: PairingRow;
  rtl: boolean;
  onEdit: () => void;
}) {
  const branchName = r.branch?.name ?? "—";
  const lo = r.loan_officer?.username ?? "—";
  const co = r.collection_officer?.username ?? "—";
  const activeCount = Array.isArray(r.active_customers) ? r.active_customers.length : 0;
  const dormantCount = Array.isArray(r.dormant_customers) ? r.dormant_customers.length : 0;

  return (
    <Card className="rounded-2xl shadow-card border overflow-hidden">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <div className="rounded-xl border bg-muted/20 p-2 shrink-0">
                <Building2 className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="min-w-0">
                <div className="font-semibold truncate">{branchName}</div>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <Badge variant="outline" className="rounded-full">
                    LO: <span className="ml-1 font-semibold text-foreground">{lo}</span>
                  </Badge>
                  <Badge variant="outline" className="rounded-full">
                    CO: <span className="ml-1 font-semibold text-foreground">{co}</span>
                  </Badge>
                </div>
              </div>
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-xl">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={rtl ? "start" : "end"} className="rounded-xl">
              <DropdownMenuItem onClick={onEdit}>
                <Pencil className={clsx("h-4 w-4", rtl ? "ml-2" : "mr-2")} />
                Edit
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <Separator className="my-3" />

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-xl border bg-muted/10 p-3">
            <div className="text-xs text-muted-foreground flex items-center gap-2">
              <Users className="h-3.5 w-3.5" /> Active Customers
            </div>
            <div className="mt-1 font-semibold">{activeCount.toLocaleString()}</div>
          </div>

          <div className="rounded-xl border bg-muted/10 p-3">
            <div className="text-xs text-muted-foreground flex items-center gap-2">
              <Users className="h-3.5 w-3.5" /> Dormant Customers
            </div>
            <div className="mt-1 font-semibold">{dormantCount.toLocaleString()}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function PairingsPage() {
  const qc = useQueryClient();

  const dir = (document.documentElement.getAttribute("dir") as "ltr" | "rtl") || "ltr";
  const rtl = dir === "rtl";

  const [q, setQ] = useState("");
  const qDebounced = useDebouncedValue(q, 350);
  const [perPage, setPerPage] = useState(25);

  // Modals
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  // Forms
  const [createForm, setCreateForm] = useState<PairingFormState>({
    branch_id: "",
    username1: "",
    username2: "",
  });

  const [editForm, setEditForm] = useState<(PairingFormState & { id: number | null })>({
    id: null,
    branch_id: "",
    username1: "",
    username2: "",
  });

  // Options: branches
  const branchesQ = useQuery({
    queryKey: ["pairing_branches_options"],
    queryFn: async () => {
      const res = await apiFetch<ApiListResponse<{ id: number; name: string }>>("/pairings/branches");
      return res.data.map((b) => ({ id: b.id, name: b.name })) as Option[];
    },
    staleTime: 60_000,
    retry: 1,
  });

  // Users by branch (Create)
  const usersCreateQ = useQuery({
    queryKey: ["pairing_users_by_branch_create", String(createForm.branch_id)],
    queryFn: async () => {
      if (!createForm.branch_id) return [] as Option[];
      const res = await apiFetch<ApiListResponse<Option>>(`/pairings/users/by-branch/${createForm.branch_id}`);
      return res.data;
    },
    enabled: !!createForm.branch_id,
    staleTime: 30_000,
    retry: 1,
  });

  // Users by branch (Edit)
  const usersEditQ = useQuery({
    queryKey: ["pairing_users_by_branch_edit", String(editForm.branch_id)],
    queryFn: async () => {
      if (!editForm.branch_id) return [] as Option[];
      const res = await apiFetch<ApiListResponse<Option>>(`/pairings/users/by-branch/${editForm.branch_id}`);
      return res.data;
    },
    enabled: !!editForm.branch_id,
    staleTime: 30_000,
    retry: 1,
  });

  // Infinite pairings
  const pairingsInf = useInfiniteQuery({
    queryKey: ["pairings_cursor", qDebounced, perPage],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      params.set("per_page", String(perPage));
      if (pageParam) params.set("cursor", String(pageParam));
      if (qDebounced?.trim()) params.set("q", qDebounced.trim());
      return apiFetch<CursorResponse<PairingRow>>(`/pairings?${params.toString()}`);
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => (lastPage?.meta?.has_more ? lastPage.meta.next_cursor : null),
    refetchOnWindowFocus: false,
    staleTime: 10_000,
  });

  const pages = pairingsInf.data?.pages ?? [];
  const rows = useMemo(() => pages.flatMap((p) => p.data || []), [pages]);
  const lastMeta = pages.length ? pages[pages.length - 1]?.meta : undefined;

  const loadingFirst = pairingsInf.isLoading;
  const loadingMore = pairingsInf.isFetchingNextPage;

  const sentinelRef = useInfiniteSentinel({
    enabled: !!pairingsInf.hasNextPage && !pairingsInf.isFetchingNextPage && !pairingsInf.isLoading,
    onLoadMore: () => {
      if (pairingsInf.hasNextPage && !pairingsInf.isFetchingNextPage) pairingsInf.fetchNextPage();
    },
  });

  // Create pairing
  const createMut = useMutation({
    mutationFn: async (payload: PairingFormState) => {
      return apiFetch("/pairings", {
        method: "POST",
        body: JSON.stringify(payload),
        headers: { "Content-Type": "application/json" } as any,
      } as any);
    },
    onSuccess: async () => {
      toast.success("Pairing created");
      setCreateOpen(false);
      setCreateForm({ branch_id: "", username1: "", username2: "" });
      await qc.invalidateQueries({ queryKey: ["pairings_cursor"] });
    },
    onError: (e: any) => toast.error(e?.message || "Failed to create pairing"),
  });

  // Edit pairing
  const editMut = useMutation({
    mutationFn: async (payload: { id: number; branch_id: any; username1: any; username2: any }) => {
      return apiFetch(`/pairings/${payload.id}`, {
        method: "PUT",
        body: JSON.stringify({
          branch_id: payload.branch_id,
          username1: payload.username1,
          username2: payload.username2,
        }),
        headers: { "Content-Type": "application/json" } as any,
      } as any);
    },
    onSuccess: async () => {
      toast.success("Pairing updated");
      setEditOpen(false);
      await qc.invalidateQueries({ queryKey: ["pairings_cursor"] });
    },
    onError: (e: any) => toast.error(e?.message || "Update failed"),
  });

  const openCreate = () => {
    setCreateForm({ branch_id: "", username1: "", username2: "" });
    setCreateOpen(true);
  };

  const openEdit = (r: PairingRow) => {
    setEditForm({
      id: r.id,
      branch_id: String(r.branch_id ?? ""),
      username1: String(r.username1 ?? ""),
      username2: String(r.username2 ?? ""),
    });
    setEditOpen(true);
  };

  const submitCreate = () => {
    const payload = {
      branch_id: createForm.branch_id,
      username1: createForm.username1,
      username2: createForm.username2,
    };

    if (!payload.branch_id) return toast.error("Branch is required");
    if (!payload.username1) return toast.error("Loan Officer is required");
    if (!payload.username2) return toast.error("Collection Officer is required");
    if (String(payload.username1) === String(payload.username2)) return toast.error("LO and CO must be different");

    createMut.mutate(payload);
  };

  const submitEdit = () => {
    if (!editForm.id) return toast.error("Missing pairing id");
    if (!editForm.branch_id) return toast.error("Branch is required");
    if (!editForm.username1) return toast.error("Loan Officer is required");
    if (!editForm.username2) return toast.error("Collection Officer is required");
    if (String(editForm.username1) === String(editForm.username2)) return toast.error("LO and CO must be different");

    editMut.mutate({
      id: editForm.id,
      branch_id: editForm.branch_id,
      username1: editForm.username1,
      username2: editForm.username2,
    });
  };

  return (
    <div dir={dir} className="w-full max-w-full overflow-x-hidden space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <motion.h1
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-2xl font-bold text-foreground"
        >
          Pairing
        </motion.h1>

        <Button
          onClick={openCreate}
          size="sm"
          className="rounded-xl bg-[#7C3AED] hover:bg-[#6D28D9] text-white shadow-[0_10px_30px_rgba(124,58,237,0.18)]"
          disabled={loadingFirst}
        >
          <Plus className={clsx("h-4 w-4", rtl ? "ml-2" : "mr-2")} />
          <span className="hidden sm:inline">Create</span>
        </Button>
      </div>

      {pairingsInf.isError ? (
        <Card className="shadow-card">
          <CardContent className="p-4 text-sm text-red-600">
            {(pairingsInf.error as any)?.message || "Failed to load pairing list"}
          </CardContent>
        </Card>
      ) : null}

      {/* Filters Card (like Users) */}
      <Card className="shadow-card overflow-hidden">
        <CardHeader className="border-b border-border/60">
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-lg">User Pairing</CardTitle>
              <Badge variant="outline" className="rounded-full text-xs">
                Loaded: <span className="ml-1 font-semibold">{rows.length}</span>
              </Badge>
            </div>

            <div className="grid grid-cols-12 gap-2 items-center">
              <div className="col-span-8 sm:col-span-9 relative min-w-0">
                <Search className={clsx("absolute top-2.5 h-4 w-4 text-muted-foreground", rtl ? "right-3" : "left-3")} />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search LO username..."
                  className={clsx("w-full rounded-xl", rtl ? "pr-10" : "pl-10")}
                />
              </div>

              <div className="col-span-4 sm:col-span-3">
                <select
                  className="h-10 w-full rounded-xl border border-border bg-background px-3 text-sm"
                  value={perPage}
                  onChange={(e) => setPerPage(Number(e.target.value))}
                >
                  {[10, 25, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      Per: {n}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Content */}
      <Card className="shadow-card overflow-hidden">
        <CardContent className="p-0">
          {/* ✅ Mobile: cards */}
          <div className="p-3 space-y-3 md:hidden">
            {loadingFirst ? (
              Array.from({ length: 6 }).map((_, i) => (
                <Card key={i} className="rounded-2xl border shadow-card">
                  <CardContent className="p-4 space-y-2">
                    <div className="h-4 w-40 rounded bg-muted animate-pulse" />
                    <div className="h-4 w-56 rounded bg-muted animate-pulse" />
                    <div className="h-16 w-full rounded-xl bg-muted animate-pulse" />
                  </CardContent>
                </Card>
              ))
            ) : rows.length === 0 ? (
              <Card className="rounded-2xl border shadow-card">
                <CardContent className="p-8 text-center text-muted-foreground">No data available</CardContent>
              </Card>
            ) : (
              <>
                {rows.map((r) => (
                  <motion.div key={r.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                    <PairingCard r={r} rtl={rtl} onEdit={() => openEdit(r)} />
                  </motion.div>
                ))}

                {loadingMore ? (
                  <div className="py-3 text-center text-sm text-muted-foreground">Loading more…</div>
                ) : null}

                <div ref={sentinelRef} />

                <div className="px-1 pb-3">
                  <Button
                    variant="outline"
                    className="w-full rounded-xl"
                    onClick={() => pairingsInf.fetchNextPage()}
                    disabled={!pairingsInf.hasNextPage || pairingsInf.isFetchingNextPage}
                  >
                    {pairingsInf.isFetchingNextPage ? "Loading..." : pairingsInf.hasNextPage ? "Load more" : "No more"}
                  </Button>
                </div>
              </>
            )}
          </div>

          {/* ✅ Desktop/iPad: table */}
          <div className="hidden md:block">
            <div className="w-full max-w-full overflow-hidden">
              <div className="overflow-x-auto overflow-y-hidden [-webkit-overflow-scrolling:touch]">
                <div className="min-w-[980px]">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b">
                      <tr className="text-left">
                        <th className="p-3 whitespace-nowrap">#</th>
                        <th className="p-3 whitespace-nowrap">Branch</th>
                        <th className="p-3 whitespace-nowrap">L.O Username</th>
                        <th className="p-3 whitespace-nowrap">C.O Username</th>
                        <th className="p-3 whitespace-nowrap">Active Customers</th>
                        <th className="p-3 whitespace-nowrap">Dormant Customers</th>
                        <th className={clsx("p-3 whitespace-nowrap", rtl ? "text-left" : "text-right")}>Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {loadingFirst ? (
                        <>
                          {Array.from({ length: 10 }).map((_, i) => (
                            <SkeletonRow key={`sk-${i}`} rtl={rtl} />
                          ))}
                        </>
                      ) : rows.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="p-10 text-center text-muted-foreground">
                            No data available
                          </td>
                        </tr>
                      ) : (
                        rows.map((r, idx) => {
                          const branchName = r.branch?.name ?? "—";
                          const lo = r.loan_officer?.username ?? "—";
                          const co = r.collection_officer?.username ?? "—";
                          const activeCount = Array.isArray(r.active_customers) ? r.active_customers.length : 0;
                          const dormantCount = Array.isArray(r.dormant_customers) ? r.dormant_customers.length : 0;

                          return (
                            <tr key={r.id} className="border-b hover:bg-muted/20">
                              <td className="p-3 text-muted-foreground">{idx + 1}</td>
                              <td className="p-3">
                                <div className="flex items-center gap-2">
                                  <Building2 className="h-4 w-4 text-muted-foreground" />
                                  <span className="font-medium">{branchName}</span>
                                </div>
                              </td>
                              <td className="p-3 font-semibold">
                                <div className="flex items-center gap-2">
                                  <User2 className="h-4 w-4 text-muted-foreground" />
                                  {lo}
                                </div>
                              </td>
                              <td className="p-3 font-semibold">
                                <div className="flex items-center gap-2">
                                  <User2 className="h-4 w-4 text-muted-foreground" />
                                  {co}
                                </div>
                              </td>
                              <td className="p-3">{activeCount.toLocaleString()}</td>
                              <td className="p-3">{dormantCount.toLocaleString()}</td>

                              <td className={clsx("p-3", rtl ? "text-left" : "text-right")}>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" className="rounded-xl">
                                      <MoreVertical className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>

                                  <DropdownMenuContent align={rtl ? "start" : "end"} className="rounded-xl">
                                    <DropdownMenuItem onClick={() => openEdit(r)}>
                                      <Pencil className={clsx("h-4 w-4", rtl ? "ml-2" : "mr-2")} />
                                      Edit
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </td>
                            </tr>
                          );
                        })
                      )}

                      {loadingMore ? (
                        <>
                          {Array.from({ length: 6 }).map((_, i) => (
                            <SkeletonRow key={`more-${i}`} rtl={rtl} />
                          ))}
                        </>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div ref={sentinelRef} />

            <div className="flex items-center justify-between gap-3 border-t border-border/60 p-4">
              <div className="text-sm text-muted-foreground">
                Loaded <span className="font-medium text-foreground">{rows.length}</span>{" "}
                {lastMeta?.has_more ? " (more available)" : " (end)"}
              </div>

              <Button
                variant="outline"
                className="rounded-xl"
                onClick={() => pairingsInf.fetchNextPage()}
                disabled={!pairingsInf.hasNextPage || pairingsInf.isFetchingNextPage}
              >
                {pairingsInf.isFetchingNextPage ? "Loading..." : pairingsInf.hasNextPage ? "Load more" : "No more"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* CREATE MODAL */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>Create Pairing</DialogTitle>
          </DialogHeader>

          <PairingForm
            form={createForm}
            setForm={setCreateForm}
            branches={branchesQ.data || []}
            users={usersCreateQ.data || []}
            loading={createMut.isPending}
            onSubmit={submitCreate}
          />

          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-xl" onClick={() => setCreateOpen(false)} disabled={createMut.isPending}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EDIT MODAL */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>Edit Pairing</DialogTitle>
          </DialogHeader>

          <PairingForm
            form={editForm}
            setForm={(cb) => setEditForm((prev) => ({ ...prev, ...cb(prev) }))}
            branches={branchesQ.data || []}
            users={usersEditQ.data || []}
            loading={editMut.isPending}
            isEdit
            onSubmit={submitEdit}
          />

          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-xl" onClick={() => setEditOpen(false)} disabled={editMut.isPending}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}